'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { toast } from '@crabs-ai/ds-react';
import {
  base64ToBlobUrl,
  usePostTtsVoicePreviewApi,
} from '@/shared/apis/tts/post-tts-voice-preview';
import { getTTSVoiceUrlApi } from '@/shared/apis/tts/get-tts-voice-url';
import { useGetTTSVoicesApi } from '@/shared/apis/tts/get-tts-voices';
import { isTtsBlockedError } from '@/shared/lib/tts-blocked-error';
import {
  DEFAULT_EMOTION,
  DEFAULT_PITCH,
} from '@/shared/constants/default-voice';
import {
  localeToLanguage,
  setPreservesPitch,
} from '@/shared/utils/audio-preview';
import type { VoicePreset } from '@/entities/voice-preset';

type CachedPreview = { url: string; revoke: boolean };

/**
 * 카드 그리드용 미리듣기 audio 매니저 (Phase B 단계 5, FU-7).
 *
 * 캡슐화한 책임:
 * - 동시 재생 race 방어 4 종 (mount / loading / fetch token / playing id ref)
 * - 미리듣기 캐시 (composite key — payload 변경 시 자동 재합성, 동일 payload 재사용)
 * - 분기: Gemini+style → POST `/v1.0/tts/voice/preview` (동적 합성, blob revoke 필요)
 *         그 외 → GET `/v1.0/tts/voice/url` (정적 S3, revoke 불필요)
 * - tempo 반영 (playbackRate) + preservesPitch (피치 보존)
 *
 * 단일 폼 라이브 미리듣기(VoicePresetFormDialog)에는 적용 안 함 — 그 쪽은
 * preset 객체가 아니라 form state 기반이라 형태가 달라 별도 단순 매니저 유지.
 *
 * 사용처: `/my-voice` 페이지, TtsDialog '내 목소리' 탭.
 */
export function useVoicePresetPreview() {
  const t = useTranslations();
  const locale = useLocale();
  const previewMutation = usePostTtsVoicePreviewApi();
  const { data: voicesData } = useGetTTSVoicesApi();

  const [playingPresetId, setPlayingPresetId] = useState<number | undefined>();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  // composite key (`buildPreviewKey`) → 캐시된 audio URL.
  // - dynamic preview 는 blob URL 이라 revoke 필요
  // - voice/url 정적 샘플은 일반 URL 이라 revoke 불필요
  // payload 가 바뀌면 키가 달라져 자연 재합성. 옛 엔트리 revoke 는 페이지 unmount 시 일괄.
  const previewCacheRef = useRef<Map<string, CachedPreview>>(new Map());
  // 언마운트 후 in-flight async 콜백이 ref/state 를 건드리는 race 방어.
  const isMountedRef = useRef(true);
  // 현재 재생 중인 preset.id 의 ref mirror. state 는 stale closure 위험이 있어
  // 분기 판단에는 ref 만 사용한다 (state 는 카드 UI 갱신 전용).
  const playingPresetIdRef = useRef<number | undefined>(undefined);
  // 동일 시점 다른 카드 클릭으로 fetch 가 겹쳐 audio 두 개 재생되는 race 방어.
  const loadingPresetIdRef = useRef<number | undefined>(undefined);
  // fetch token — 매 fetch 마다 단조 증가. 취소(같은 카드 토글) 시 bump 해서
  // 진행 중 fetch 의 myToken 과 어긋나도록 만들어 후속 처리(toast/cache/state) 를 무효화.
  const fetchTokenRef = useRef(0);

  const voiceVendorByCode = useMemo(() => {
    const map = new Map<string, string>();
    voicesData?.data?.forEach((v) => map.set(v.code, v.vendor));
    return map;
  }, [voicesData]);

  useEffect(() => {
    // mount-time 리셋: hot-reload 시 이전 인스턴스의 cleanup 으로 false 가 된 ref 가
    // 새 인스턴스에 그대로 남아 fetch 콜백이 toast/state 갱신을 스킵하는 race 회피.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      audioRef.current?.pause();
      audioRef.current = null;
      previewCacheRef.current.forEach(({ url, revoke }) => {
        if (revoke) URL.revokeObjectURL(url);
      });
      previewCacheRef.current.clear();
    };
  }, []);

  const stopPreview = useCallback(() => {
    audioRef.current?.pause();
    audioRef.current = null;
    playingPresetIdRef.current = undefined;
    setPlayingPresetId(undefined);
  }, []);

  /**
   * preview 캐시 키 — `resolvePreviewUrl` 의 분기·요청 입력값 composite.
   * preset.id 제외: 동일 payload 다른 preset (예: duplicate, SYSTEM 동일 payload) 가 같은 음원 공유.
   * payload 가 변경된 동일 id 는 자연스럽게 새 키로 join 안 되어 재합성.
   * 구분자는 `\x00` — `style` 이 자연어 텍스트라 일반 ASCII 구분자(`|`, `:`) 충돌 가능.
   */
  const buildPreviewKey = useCallback(
    (preset: VoicePreset): string => {
      const language = localeToLanguage(locale, preset.payload.languageCode);
      return [
        preset.payload.code,
        preset.payload.style?.trim() ?? '',
        preset.payload.emotionCode ?? '',
        language,
        preset.payload.pitch ?? '',
        preset.payload.tempo,
      ].join('\x00');
    },
    [locale],
  );

  const resolvePreviewUrl = useCallback(
    async (preset: VoicePreset): Promise<CachedPreview> => {
      const language = localeToLanguage(locale, preset.payload.languageCode);
      const vendor = voiceVendorByCode.get(preset.payload.code);
      const trimmedStyle = preset.payload.style?.trim();
      const useDynamic = vendor === 'gemini' && !!trimmedStyle;

      if (useDynamic) {
        const response = await previewMutation.mutateAsync({
          voice: preset.payload.code,
          text: t('Common.component.dialog.tts-dialog.preview-sample-text'),
          emotion: preset.payload.emotionCode ?? undefined,
          style: trimmedStyle,
          language,
        });
        return {
          url: base64ToBlobUrl(
            response.data.audioBase64,
            response.data.mime || 'audio/wav',
          ),
          revoke: true,
        };
      }

      const response = await getTTSVoiceUrlApi({
        voice: preset.payload.code,
        language,
        pitch: preset.payload.pitch ?? DEFAULT_PITCH,
        emotion: preset.payload.emotionCode ?? DEFAULT_EMOTION,
        speedFactor: preset.payload.tempo,
      });
      return { url: response.data, revoke: false };
    },
    [locale, previewMutation, t, voiceVendorByCode],
  );

  const showPreviewErrorToast = useCallback(() => {
    toast({
      type: 'error',
      description: t('MyVoicePage.preview-error'),
      toastOptions: { position: 'top-center' },
    });
  }, [t]);

  const playPreview = useCallback(
    async (preset: VoicePreset) => {
      // voicesData 미로드 시 vendor 매핑 실패 → Gemini+style preset 이 정적 S3 fallback 으로
      // 잘못된 음원 재생 (silent 데이터 무결성 위험). 도착 전에는 미리듣기 차단 + toast.
      if (!voicesData?.data || voicesData.data.length === 0) {
        showPreviewErrorToast();
        return;
      }
      // 같은 카드 클릭 — 재생 중이거나 로딩 중이면 모두 정지/취소.
      // fetchTokenRef bump 로 진행 중 fetch 의 후속 처리(toast/cache/state) 를 무효화.
      // ref 기반 비교로 React state 갱신 사이클 사이의 stale closure race 회피.
      if (
        playingPresetIdRef.current === preset.id ||
        loadingPresetIdRef.current === preset.id
      ) {
        stopPreview();
        fetchTokenRef.current += 1;
        loadingPresetIdRef.current = undefined;
        return;
      }
      // 다른 카드의 fetch 가 진행 중이면 무시 (audio 다중 재생 race 방어).
      if (loadingPresetIdRef.current !== undefined) {
        return;
      }
      stopPreview();

      const cacheKey = buildPreviewKey(preset);
      let cached = previewCacheRef.current.get(cacheKey);
      if (!cached) {
        const myToken = ++fetchTokenRef.current;
        loadingPresetIdRef.current = preset.id;
        try {
          cached = await resolvePreviewUrl(preset);
        } catch (error) {
          // 취소(myToken 무효)·언마운트 사이에 도달한 에러는 무시.
          if (fetchTokenRef.current === myToken) {
            // 422 정책 차단(1031/1032) 은 전역 TtsBlockedDialog 가 흡수 — 추가 toast skip.
            if (isMountedRef.current && !isTtsBlockedError(error)) {
              showPreviewErrorToast();
            }
            loadingPresetIdRef.current = undefined;
          }
          return;
        }
        // 취소·언마운트 — blob 누수 없이 abort.
        if (fetchTokenRef.current !== myToken || !isMountedRef.current) {
          if (cached.revoke) URL.revokeObjectURL(cached.url);
          if (fetchTokenRef.current === myToken) {
            loadingPresetIdRef.current = undefined;
          }
          return;
        }
        previewCacheRef.current.set(cacheKey, cached);
        loadingPresetIdRef.current = undefined;
      }

      const audio = new Audio(cached.url);
      audio.playbackRate = preset.payload.tempo || 1.0;
      setPreservesPitch(audio, true);
      audio.onended = () => {
        if (audioRef.current === audio) {
          audioRef.current = null;
          playingPresetIdRef.current = undefined;
          setPlayingPresetId(undefined);
        }
      };
      audioRef.current = audio;
      playingPresetIdRef.current = preset.id;
      setPlayingPresetId(preset.id);

      try {
        await audio.play();
      } catch {
        if (audioRef.current === audio) {
          audioRef.current = null;
          playingPresetIdRef.current = undefined;
          if (isMountedRef.current) setPlayingPresetId(undefined);
        }
        if (isMountedRef.current) showPreviewErrorToast();
      }
    },
    [buildPreviewKey, resolvePreviewUrl, showPreviewErrorToast, stopPreview],
  );

  return { playingPresetId, playPreview, stopPreview };
}
